import cn from 'classnames';
import React, { useContext, useMemo, useCallback } from 'react';
import { SEARCH_RESULT_FIRST_COLUMN_CONFIG } from '@/creditflowresearch/application/SearchHeaderMenu/constants';
import ResultsColumn, { ResultsItem } from './ResultsColumn';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton/MissingSomethingModalButton';
import useArrowNavigation from '@/creditflowresearch/ui/common/components/organisms/SearchResultsMenu/helpers/useArrowNavigation';
import { useHeaderMenuContext } from '@/creditflowresearch/application/SearchHeaderMenu/HeaderMenuContext';
import { getMappedSearchData } from '@/creditflowresearch/ui/common/components/organisms/SearchResultsMenu/helpers/mappers';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useHeaderMenuContext as useHeaderMenuContextV2 } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';

import styles from './searchResults.module.scss';
import columnStyles from './ResultsColumn/resultsColumn.module.scss';

interface IProps {
  searchText: string,
  searchResult: any,
  isSearchFetching: boolean,
  onSearchResultItemClick: () => void,
  variant?: 'default' | 'creditflow',
}

const renderNoSearchResult = (searchText, variant) => (
  <div
    className={ cn(styles.noSearchResults, {
      [styles.creditflowVariant]: variant === 'creditflow',
    }) }
    data-test="noSearchResultsContent"
  >
    <div>
      No results found for "<span className={ styles.searchText }>{ searchText }</span>".
    </div>
    <div>
      <MissingSomethingModalButton />
    </div>
  </div>
);

const SearchResults = (props: IProps) => {
  const { setIsSearchResultsOpen } = useHeaderMenuContext();
  const legacy = useContext(DictionariesContext);
  const { products: v2Products, regions: v2Regions } = useHeaderMenuContextV2();
  const products = legacy?.productsList ?? v2Products;
  const regions = legacy?.regionsList ?? v2Regions;
  const reversedRegions = useMemo(() => [...regions].reverse(), [regions]);

  const searchResultsMatrix = useMemo(() => {
    if (!props.isSearchFetching && props.searchResult.hasData && reversedRegions.length) {
      return getMappedSearchData(props.searchResult, products, reversedRegions);
    }
    return null;
  }, [props.isSearchFetching, props.searchResult, products, reversedRegions]);

  const activeItemName = useArrowNavigation(
    searchResultsMatrix,
    (props.searchResult.hasData || !props.isSearchFetching),
  );

  // @ts-ignore
  const onSearchResultItemClick = useCallback(() => {
    // @ts-ignore
    setIsSearchResultsOpen(false);
    if (typeof props.onSearchResultItemClick === 'function') {
      props.onSearchResultItemClick();
    }
  });

  return (
    <div className={ cn(styles.resultsContainer, { [styles.noResults]: !props.searchResult.hasData }) }>
      {
        props.searchResult.hasData
          ? (
            <div
              className={ styles.row }
              data-test="searchResultsContent"
            >
              { props.searchResult.hasFirstColumnData && (
                <div className={ columnStyles.column }>
                  { SEARCH_RESULT_FIRST_COLUMN_CONFIG.map((item) => (
                    <ResultsItem
                      // @ts-ignore
                      activeItemName={ activeItemName }
                      key={ item.key }
                      title={ item.title }
                      data={ props.searchResult[item.key]?.collection }
                      onClick={ onSearchResultItemClick }
                      hideOnNoResults
                    />
                  )) }
                </div>
              ) }
              { products.map((product) => (
                <ResultsColumn
                  activeItemName={ activeItemName }
                  key={ product.id }
                  product={ product }
                  onSearchResultItemClick={ onSearchResultItemClick }
                  variant={ props.variant }
                />
              )) }
            </div>
          )
          : !props.isSearchFetching && renderNoSearchResult(props.searchText, props.variant)
      }
    </div>
  );
};

export default SearchResults;
